import {
  HomeIcon,
  LinkIcon,
  SparklesIcon,
  CloudIcon,
  InboxIcon,
  CreditCardIcon,
  CpuChipIcon,
  ChatBubbleLeftIcon,
  ChartBarSquareIcon,
  BoltIcon,
  BellAlertIcon,
  CubeIcon,
  PhoneIcon,
  RectangleStackIcon,
  UsersIcon,
  ShieldCheckIcon,
  RocketLaunchIcon,
  ScaleIcon,
  CircleStackIcon,
  ClipboardDocumentCheckIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';

import { canReviewMarketplaceExecutions } from '../../services/marketplaceReview';
import { canPublishMarketplaceOffers } from '../../services/marketplacePublish';

/*
 * What the platform sidebar contains, for a given viewer, as data.
 *
 * This used to live inside Layout, which is fine while Layout is the only thing
 * that draws a sidebar. It stopped being the only thing: the marketing homepage
 * mounts the real Dashboard behind its Control surface, and a dashboard without
 * the platform nav beside it is not the product — it is a panel. Copying the
 * array into the marketing tree would mean a nav item added to the app silently
 * failing to appear in the picture the homepage uses to sell it, which is the
 * exact class of drift that makes screenshots lie.
 *
 * So the model is here, both consumers read it, and the only thing Layout kept
 * is what Layout alone has: routing, auth, and its own chrome.
 *
 * Pure: it takes the viewer and the hostname and returns items. Nothing here
 * reads window, so it renders identically on the server and in a test.
 */

/**
 * The audience flags every visibility decision below is written against.
 * Exported because the same questions are asked outside the nav, and because a
 * test that pins the flags is far more legible than one that pins their effects.
 */
export function resolvePlatformAudience(user, hostname = '') {
  const tenantType = user?.tenant?.tenant_type || user?.tenant_type || user?.user_type;
  const isBluebearEmployee = user?.isBluebearEmployee ?? user?.is_bluebear_employee;
  const host = String(hostname || '');
  const isLocalHost = host === 'localhost' || host === '127.0.0.1';
  const isExecgptHost = host.endsWith('execgpt.com') || host.includes('execgpt');
  const isBluebearHost = host.endsWith('bluebear.ai');

  const platformRoles = new Set(['admin', 'platform', 'tenant']);
  const isPlatformUser = platformRoles.has(user?.role) || isBluebearEmployee || tenantType === 'platform';

  const isWhiteLabelUser = ['white-label', 'reseller'].includes(tenantType) || isPlatformUser;
  const isDirectCustomer = tenantType === 'end-customer';

  return {
    tenantType,
    isPlatformUser,
    isDirectCustomer,
    isRegularUser: user?.role === 'user',
    isAdmin: user?.role === 'admin',
    /* The marketplace attestation queue. Permission-gated, never role-gated:
     * the gateway keeps `review_marketplace_executions` out of ROLE_PERMISSIONS
     * on purpose, so the nav entry appears only for a viewer who actually
     * carries the grant. */
    canReviewMarketplace: canReviewMarketplaceExecutions(user),
    /* The seller workspace. Same rule: `publish_marketplace_offers` is not in
     * ROLE_PERMISSIONS because publishing is invite-only during the pilot, so
     * the entry appears only for a viewer who actually carries the grant. */
    canPublishMarketplace: canPublishMarketplaceOffers(user),
    canManageVoiceInfra:
      user?.role === 'admin' || user?.role === 'bluebear_admin' || user?.role === 'tenant',
    showCloudManager: isLocalHost || (isBluebearHost && !isExecgptHost),
    showWhiteLabelTab: isLocalHost || isWhiteLabelUser,
    showWhiteLabelRequest: !(isLocalHost || isWhiteLabelUser) && (isBluebearHost || isLocalHost),
  };
}

/**
 * The sidebar for one viewer: the main item list (already filtered) and the
 * collapsible Admin group that sits directly under Dashboard.
 *
 * `section` on an item opens a labelled group at that point in the list — it is
 * a heading, not a container, so the list stays flat and a new group is one
 * property rather than a new nesting level.
 */
export function buildPlatformNav({ user, hostname = '' } = {}) {
  const audience = resolvePlatformAudience(user, hostname);

  const items = [{ name: 'Dashboard', href: '/dashboard', icon: HomeIcon }];

  if (audience.isDirectCustomer) {
    items.push({ name: 'Compute', href: '/cloud-manager', icon: CloudIcon });
  } else if (audience.isPlatformUser) {
    items.push({ name: 'Cloud Manager', href: '/cloud-manager', icon: CloudIcon });
  }

  items.push(
    { name: 'Credits', href: '/credits', icon: CreditCardIcon },
    { name: 'Integrations', href: '/integrations', icon: LinkIcon },
    { name: 'Models', href: '/models', icon: CpuChipIcon, section: 'AI Platform' },
    { name: 'Workspaces', href: '/workspaces', icon: CubeIcon },
    { name: 'Agents', href: '/ai-agents', icon: BoltIcon },
    { name: 'Workflows', href: '/workflows', icon: RectangleStackIcon },
    { name: 'Chat', href: '/ai-chat', icon: ChatBubbleLeftIcon },
    { name: 'Sessions', href: '/ai-sessions', icon: ChartBarSquareIcon },
    { name: 'Invocations', href: '/invocations', icon: BoltIcon },
    { name: 'Policies', href: '/routing-rules', icon: ScaleIcon },
  );

  if (audience.canReviewMarketplace) {
    items.push({ name: 'Report Review', href: '/marketplace/review', icon: ClipboardDocumentCheckIcon });
  }

  if (audience.canPublishMarketplace) {
    items.push({ name: 'Marketplace', href: '/marketplace/sell', icon: BanknotesIcon });
  }

  if (audience.canManageVoiceInfra) {
    items.push({ name: 'Voice Infrastructure', href: '/voice-infrastructure', icon: PhoneIcon });
  }

  if (audience.showWhiteLabelTab) {
    items.push({ name: 'White-Label', href: '/white-label', icon: SparklesIcon });
  } else if (audience.showWhiteLabelRequest) {
    items.push({ name: 'Request White-Label', href: '/white-label-request', icon: InboxIcon });
  }

  if (audience.isRegularUser) {
    items.push({ name: 'Waitlist', href: '/admin/waitlist', icon: InboxIcon });
  }

  const adminItems = audience.isAdmin
    ? [
        { name: 'Manage Users', href: '/admin/users', icon: UsersIcon },
        { name: 'Routing Policies', href: '/admin/routing-policies', icon: ScaleIcon },
        { name: 'Workspace Memory', href: '/admin/workspace-memory', icon: CircleStackIcon },
        { name: 'Tenant Features', href: '/admin/tenant-features', icon: ShieldCheckIcon },
        { name: 'Releases', href: '/admin/releases', icon: RocketLaunchIcon },
        { name: 'Notifications', href: '/admin/notifications', icon: BellAlertIcon },
      ]
    : [];

  /* Host-level visibility is applied after the build rather than inside it, so
   * that a role that earns an item and a host that forbids it stay two separate
   * readable rules. */
  const visible = items.filter((item) => {
    if (item.href === '/cloud-manager') return audience.showCloudManager;
    if (item.href.startsWith('/white-label')) {
      return audience.showWhiteLabelTab || audience.showWhiteLabelRequest;
    }
    return true;
  });

  return { audience, items: visible, adminItems };
}

/** True when `pathname` is the item's own route or something beneath it. */
export function isNavItemActive(pathname, href) {
  return pathname === href || String(pathname || '').startsWith(`${href}/`);
}

export default buildPlatformNav;
